import type { ComponentType, SVGProps } from "react";
import { Link } from "react-router-dom";
import {
  BriefcaseIcon,
  CalculatorIcon,
  ChevronRightIcon,
  ClockIcon,
  UserCircleIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import type { PersonalHubCard } from "@/lib/personalNav";

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const CARD_ICONS: Record<string, IconComponent> = {
  "mein-bereich": UserCircleIcon,
  zeiterfassung: ClockIcon,
  mitarbeiter: UsersIcon,
  personalkosten: CalculatorIcon,
  nachunternehmer: BriefcaseIcon,
};

/** Kurze Orientierung, was in dem Bereich zu finden ist. */
const CARD_TAGS: Record<string, string[]> = {
  "mein-bereich": ["Erfassen", "Abwesenheit", "Monat", "Jahr"],
  zeiterfassung: ["Übersicht", "Stundenkonten", "Freigaben"],
  mitarbeiter: ["Kontingente", "Startstände", "Stammdaten"],
  personalkosten: ["Laufende Kosten", "Stundensatz"],
  nachunternehmer: ["Zeiten prüfen", "Rechnungen", "Bezahlt"],
};

const CARD_CLASS =
  "group relative flex h-full min-h-[9.5rem] flex-col gap-3 overflow-hidden rounded-ios border border-separator bg-card p-5 shadow-ios transition hover:-translate-y-0.5 hover:border-accent hover:shadow-ios-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent";

export function PersonalAreaCards({ cards }: { cards: PersonalHubCard[] }) {
  if (cards.length === 0) return null;
  return (
    <section aria-label="Bereiche" className="space-y-3">
      <div className="flex items-baseline justify-between gap-3 px-0.5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-label-tertiary">
          Bereiche
        </h2>
        <span className="text-xs text-label-tertiary">
          {cards.length === 1 ? "1 Bereich" : `${cards.length} Bereiche`}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {cards.map((card) => {
          const Icon = CARD_ICONS[card.key] ?? UsersIcon;
          const tags = CARD_TAGS[card.key] ?? [];
          const inner = (
            <>
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                style={{
                  background:
                    "radial-gradient(circle at top right, color-mix(in srgb, var(--eh-accent) 14%, transparent), transparent 60%)",
                }}
              />
              <div className="flex items-start justify-between gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent transition group-hover:bg-accent group-hover:text-label-on-accent">
                  <Icon className="h-[22px] w-[22px]" aria-hidden />
                </span>
                <ChevronRightIcon
                  aria-hidden
                  className="mt-1 h-5 w-5 shrink-0 text-label-tertiary transition group-hover:translate-x-0.5 group-hover:text-accent"
                />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-label">{card.title}</h3>
                <p className="mt-1 text-sm leading-snug text-label-secondary">
                  {card.description}
                </p>
              </div>
              {tags.length > 0 ? (
                <ul className="mt-auto flex flex-wrap gap-1.5 pt-1" aria-label="Inhalte">
                  {tags.map((tag) => (
                    <li
                      key={tag}
                      className="rounded-full bg-fill-tertiary px-2 py-0.5 text-[11px] font-medium text-label-secondary"
                    >
                      {tag}
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          );

          if (card.internal) {
            return (
              <Link key={card.key} to={card.href} className={CARD_CLASS}>
                {inner}
              </Link>
            );
          }
          return (
            <a key={card.key} href={card.href} className={CARD_CLASS}>
              {inner}
            </a>
          );
        })}
      </div>
    </section>
  );
}
